import { AxiosResponse } from 'axios';
import { action, runInAction } from 'mobx';

import { ResultKind, ServiceStatus } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError, setCSRFToken } from 'services/base/http';

import { FCMService } from 'services/accounts/fcm_service';

import {
  IAccountsDomain,
  InvoiceMap,
  PangolinMap,
  PaymentMap,
  createInvoiceObjects,
  createPangolinObjects,
  createPaymentObjects,
  createPricesObjects,
  createUserObject,
  pangolinSorter,
  paymentSorter,
} from './types';

import { CreateSessionRequest, SessionResponse } from './protos/session_pb';

export type FetchResult =
  | { kind: ResultKind.OK; authenticated: boolean }
  | { kind: ResultKind.SYSTEM_ERROR };

export type LoginResult =
| { kind: ResultKind.OK }
| { kind: ResultKind.SYSTEM_ERROR }
| {
  kind: ResultKind.USER_ERROR;
  passwordError?: string;
};

export type LogoutResult =
| { kind: ResultKind.OK }
| { kind: ResultKind.USER_ERROR }
| { kind: ResultKind.SYSTEM_ERROR };

export class SessionService {
  @action
  static async fetch(domain: IAccountsDomain): Promise<FetchResult> {
    // IMPORTANT:
    // This MUST be called when the page initially loads.
    // The CSRF cookie is set by the session get view.
    //
    // Checks if the user is already logged in.
    // Also fills in the user and pangolins on the domain
    // ResultKind.OK - Session was successfully retrieved
    // ResultKind.SYSTEM_ERROR - Network failure or something
    domain.status = ServiceStatus.LOADING;
    try {
      const httpResp = await http.get('/session/');
      return runInAction(() => {
        const { authenticated } = applySessionResponse(domain, httpResp);
        FCMService.update();
        domain.status = ServiceStatus.LOADED;
        return { kind: ResultKind.OK, authenticated };
      });
    } catch (error) {
      if (isHttpError(error)) {
        if (error.response?.status && error.response.status < 500) {
          runInAction(() => {
            domain.status = ServiceStatus.LOADED;
          });
          return { kind: ResultKind.OK, authenticated: false };
        }
      }
      runInAction(() => {
        domain.status = ServiceStatus.FRESH;
      });
      return { kind: ResultKind.SYSTEM_ERROR };
    }
  }

  static async login(
    domain: IAccountsDomain,
    email: string,
    password: string,
  ): Promise<LoginResult> {
    // Logs the user into the application and fills in the user and pangolins on the domain domain
    // ResultKind.OK - Successful login
    // ResultKind.USER_ERROR - The user failed to login (wrong password)
    // ResultKind.SYSTEM_ERROR - Network failure or something
    try {
      const request = createCreateSessionRequest(email, password);
      const httpResp = await http.post('/session/', createRequestString(request));
      return runInAction(() => {
        applySessionResponse(domain, httpResp);
        FCMService.update();
        return { kind: ResultKind.OK };
      });
    } catch (error) {
      if (isHttpError(error)) {
        if (error.response?.status && error.response.status < 500) {
          const httpResp = error.response;
          const response = SessionResponse.deserializeBinary(getResponseBytes(httpResp));
          return {
            kind: ResultKind.USER_ERROR,
            passwordError: response.getPasswordError(),
          };
        }
      }
      return { kind: ResultKind.SYSTEM_ERROR };
    }
  }

  static async logout(domain: IAccountsDomain): Promise<LogoutResult> {
    // Logs the user out of the application and clears the domain domain
    // ResultKind.OK - Successful logout
    // ResultKind.USER_ERROR - The user was not logged in to begin with
    // ResultKind.SYSTEM_ERROR - Network failure or something
    try {
      const httpResp = await http.delete('/session/');
      runInAction(() => {
        clearSession(domain, httpResp);
      });
      return { kind: ResultKind.OK };
    } catch (error) {
      if (isHttpError(error)) {
        if (error.response?.status && error.response.status < 500) {
          const httpResp = error.response;
          runInAction(() => {
            clearSession(domain, httpResp);
          });
          return { kind: ResultKind.USER_ERROR };
        }
      }
      return { kind: ResultKind.SYSTEM_ERROR };
    }
  }
}

function applySessionResponse(domain: IAccountsDomain, httpResp: AxiosResponse): { authenticated: boolean } {
  const response = SessionResponse.deserializeBinary(getResponseBytes(httpResp));
  return sharedApplySessionResponse(domain, response);
}

export function sharedApplySessionResponse(domain: IAccountsDomain, response: SessionResponse): { authenticated: boolean } {
  const userDTO = response.getUser();
  domain.user = userDTO ? createUserObject(userDTO) : undefined;
  const pangolins = createPangolinObjects(response.getPangolinsList());
  const payments = createPaymentObjects(response.getPaymentsList());
  const invoices = createInvoiceObjects(response.getInvoicesList());
  const pricesDTO = response.getPrices();
  domain.prices = pricesDTO ? createPricesObjects(pricesDTO) : undefined;

  pangolins.sort(pangolinSorter);
  payments.sort(paymentSorter);

  domain.pangolins = new PangolinMap(
    pangolins.map(p => [p.id, p])
  );

  domain.payments = new PaymentMap(
    payments.map(p => [p.id, p])
  );

  domain.invoices = new InvoiceMap(
    invoices.map(i => [i.id, i])
  );

  const csrfToken = response.getCsrfToken();
  if (csrfToken) {
    setCSRFToken(csrfToken);
  }
  return { authenticated: userDTO != null };
}

function clearSession(domain: IAccountsDomain, httpResp: AxiosResponse) {
  const response = SessionResponse.deserializeBinary(getResponseBytes(httpResp));
  sharedClearSessionResponse(domain, response);
}

export function sharedClearSessionResponse(domain: IAccountsDomain, response: SessionResponse) {
  domain.user = undefined;
  domain.pangolins = new PangolinMap();
  domain.payments = new PaymentMap();
  domain.invoices = new InvoiceMap();
  const csrfToken = response.getCsrfToken();
  if (csrfToken) {
    setCSRFToken(csrfToken);
  }
}

function createCreateSessionRequest(email: string, password: string): CreateSessionRequest {
  const request = new CreateSessionRequest();
  request.setEmail(email.trim());
  request.setPassword(password);
  return request;
}
